import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ref as dbRef, get, update } from 'firebase/database';
import { database } from '../../Firebase/firebaseconfig';
import { ArrowLeft, Save, Plus } from 'lucide-react';
import './admin-page.css';

const RESERVED = ['Name', 'Price', 'Cover_image', 'Images'];

export default function EditComponentDetails() {
  const { compKey } = useParams();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [details, setDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    get(dbRef(database, `components/${compKey}`))
      .then(snap => {
        if (snap.exists()) {
          const data = snap.val();
          setName(data.Name || '');
          setPrice(String(data.Price || ''));
          const extras = Object.entries(data)
            .filter(([k]) => !RESERVED.includes(k))
            .map(([k, v]) => ({ key: k, value: String(v) }));
          setDetails(extras.length > 0 ? extras : [{ key: '', value: '' }]);
        } else {
          setError('Component not found.');
        }
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [compKey]);

  const handleDetailChange = (i, field, val) =>
    setDetails(prev => prev.map((d, idx) => idx === i ? { ...d, [field]: val } : d));
  const addDetail = () => setDetails(prev => [...prev, { key: '', value: '' }]);
  const removeDetail = (i) => setDetails(prev => prev.filter((_, idx) => idx !== i));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name || !price) return setError('Name and Price are required.');
    setError(''); setSuccess(''); setSaving(true);
    try {
      const extraDetails = {};
      details.forEach(d => {
        if (d.key.trim()) extraDetails[d.key.trim()] = d.value.trim();
      });
      await update(dbRef(database, `components/${compKey}`), {
        Name: name,
        Price: Number(price),
        ...extraDetails,
      });
      setSuccess('Component updated successfully!');
    } catch (err) {
      setError('Save failed: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="ap-loading">Loading…</div>;

  return (
    <div className="ap-page">
      <div className="ap-header">
        <button className="ap-back-btn" onClick={() => navigate('/admin/dashboard/edit-component')}><ArrowLeft size={18} /></button>
        <div>
          <h1 className="ap-title">Edit Component</h1>
          <p className="ap-subtitle">{name}</p>
        </div>
      </div>

      {error && <div className="ap-error">{error}</div>}
      {success && <div className="ap-success">{success}</div>}

      <div className="ap-card">
        <form onSubmit={handleSave}>
          <div className="ap-row">
            <div className="ap-form-group">
              <label className="ap-label">Component Name</label>
              <input className="ap-input" value={name} onChange={e => setName(e.target.value)} />
            </div>
            <div className="ap-form-group">
              <label className="ap-label">Price (₹)</label>
              <input className="ap-input" type="number" value={price} onChange={e => setPrice(e.target.value)} />
            </div>
          </div>

          <p className="ap-section-title">Additional Details</p>
          {details.map((d, i) => (
            <div className="ap-detail-row" key={i}>
              <input className="ap-input" placeholder="Key" value={d.key} onChange={e => handleDetailChange(i, 'key', e.target.value)} />
              <input className="ap-input" placeholder="Value" value={d.value} onChange={e => handleDetailChange(i, 'value', e.target.value)} />
              {details.length > 1 && (
                <button type="button" className="ap-detail-row-remove" onClick={() => removeDetail(i)}>✕</button>
              )}
            </div>
          ))}
          <button type="button" className="ap-btn ap-btn-ghost" style={{ marginTop: 6, fontSize: '0.82rem', padding: '6px 14px', display:'flex', alignItems:'center', gap:5 }} onClick={addDetail}>
            <Plus size={13} /> Add Detail
          </button>

          <div className="ap-btn-row">
            <button type="submit" className="ap-btn ap-btn-primary" disabled={saving} style={{ display:'flex', alignItems:'center', gap:6 }}>
              <Save size={15} /> {saving ? 'Saving…' : 'Save Changes'}
            </button>
            <button type="button" className="ap-btn ap-btn-ghost" onClick={() => navigate('/admin/dashboard/edit-component')}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}
